import axios from "axios";
import {captureError} from "./sentry";

axios.interceptors.request.use(function (config) {
    let currentHostname = window.location.origin
        .replace('www.', '')
        .replace('http://', '')
        .replace('https://', '');
    if (window.location.href.indexOf('localhost') > -1 && process.env.PREACT_APP_LOCALHOST_CURRENT_HOSTNAME) {
        currentHostname = process.env.PREACT_APP_LOCALHOST_CURRENT_HOSTNAME;
    }
    const isPosIntegration = config.url && config.url.indexOf('/v1/integration/') > -1;
    const hasUrlParam = (config.params && config.params.url) ||
        (config.url && config.url.indexOf('url=') > -1);
    if (!hasUrlParam && !isPosIntegration) {
        if (config.url.indexOf('?') === -1) {
            config.url += `?url=${currentHostname}`;
        } else {
            config.url += `&url=${currentHostname}`;
        }
    }

    const isDnsResolver = config.url && config.url.indexOf('dns-resolver') > -1;
    if (!isDnsResolver) {
        const stored = localStorage.getItem('callCenterToken');
        if (stored) {
            try {
                const tokenData = JSON.parse(stored);
                config.headers.callcentertoken = tokenData.token;
            } catch (e) {}
        }

        const customerStored = localStorage.getItem('customerToken');
        if (customerStored) {
            try {
                const tokenData = JSON.parse(customerStored);
                config.headers.authorizationtoken = tokenData.token;
            } catch (e) {}
        }

        // POS bearer token — only for /v1/integration/* (the API Integration Portal surface)
        // shared by Windows POS clients. Distinguishes browser POS from Windows via X-Client-Type.
        if (config.url && config.url.indexOf('/v1/integration/') > -1) {
            const posStored = localStorage.getItem('posDevice');
            if (posStored) {
                try {
                    const device = JSON.parse(posStored);
                    if (device?.accessToken) {
                        config.headers['Authorization'] = `Bearer ${device.accessToken}`;
                        config.headers['X-Client-Type'] = 'bro-pos-web';
                        if (device.deviceFingerprint) {
                            config.headers['X-Device-Fingerprint'] = device.deviceFingerprint;
                        }
                    }
                } catch (e) {}
            }
        }
    }

    return config;
}, function (error) {
    return Promise.reject(error);
});

// Routes reachable from an API-generated payment link (checkout-session, promise-to-pay,
// enrollment, manage-payment-methods, invoice, receipt) plus /version. None of these require
// call-center authentication, and none of them may ever be ejected to '/' — '/' is the public
// web portal, which a merchant can disable (publicWebPortalEnabled === false), turning the
// redirect into a dead end on a live payment page.
//
// Matching is TRUE PREFIX matching on whole path SEGMENTS — an entry matches the path itself
// or any deeper sub-path (so tokenized routes like /checkout-session/<token> match), and
// NOTHING else. It is deliberately NOT substring matching: a substring test would exempt any
// future route that merely CONTAINS one of these tokens somewhere in the middle (e.g. an
// /account/receipts page would match '/receipt'), silently losing the stale-token clear and the
// login redirect that portalsback's RequireCallCenterSession relies on.
//
// Because matching is per-segment, '/invoice' does NOT cover '/invoice-status' — the two are
// listed separately.
export const CALL_CENTER_REDIRECT_EXEMPT_PATH_PREFIXES = [
    '/invoice',                 // /invoice/:token
    '/invoice-status',          // /invoice-status/:token — a SIBLING of /invoice, not a sub-path
    '/checkout-session',
    '/promise-to-pay',
    '/enroll',
    '/customer/external',       // .../payment-methods and .../payment-methods/add-only
    '/receipt',
    '/version',
];

export function isCallCenterRedirectExemptPath(pathname) {
    if (typeof pathname !== 'string') return false;
    // Normalize a trailing slash so '/receipt/' behaves exactly like '/receipt'.
    const normalized = pathname.length > 1 && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;
    return CALL_CENTER_REDIRECT_EXEMPT_PATH_PREFIXES.some((prefix) => {
        // Tolerate a trailing slash in the table itself (e.g. '/customer/external/').
        const base = prefix.length > 1 && prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
        return normalized === base || normalized.startsWith(`${base}/`);
    });
}

axios.interceptors.response.use(
    function (response) {
        // Channel-awareness (2026-08-17 incident follow-up): every page fetches
        // `portal/get-info`, and that payload carries `isCallCenterLogin`. When
        // the operator turns call-center mode OFF for this portal, drop the
        // stored agent token on the spot so it is never attached to another
        // request from this browser — a stale token on a disabled portal grants
        // nothing (backend degrades it to public since aa458c2f2) but it kept
        // agents in a half-logged-in limbo and fed stale-header edge paths.
        // `=== false` only: absent means the portal never had call-center mode,
        // and a missing field on an error/partial payload must not log anyone out.
        try {
            const url = (response && response.config && response.config.url) || '';
            if (url.indexOf('portal/get-info') > -1 &&
                response.data && response.data.isCallCenterLogin === false &&
                localStorage.getItem('callCenterToken')) {
                localStorage.removeItem('callCenterToken');
                console.info('[call-center] portal has call-center mode off — cleared stored agent token');
            }
        } catch (e) { /* never break a response on cleanup */ }
        return response;
    },
    function (error) {
        captureError(error, { type: 'axios-response-error' });
        if (error.response && error.response.status === 401 &&
            error.response.data && error.response.data.error === 'Call center login required') {
            // Don't redirect on API-generated-link routes — they don't need call center auth,
            // and '/' may be a disabled public web portal.
            const isExemptRoute = isCallCenterRedirectExemptPath(window.location.pathname);
            if (!isExemptRoute) {
                localStorage.removeItem('callCenterToken');
                // Redirect to home instead of reloading (prevents infinite loop on /history)
                if (window.location.pathname !== '/') {
                    window.location.href = '/';
                } else {
                    window.location.reload();
                }
            }
        }
        // Handle customer auth 401 on /account route
        if (error.response && error.response.status === 401 &&
            window.location.pathname.indexOf('/account') > -1) {
            localStorage.removeItem('customerToken');
            window.location.href = '/login';
        }
        // POS bearer 401 — accessToken expired/revoked. Clear device bond; user re-binds.
        if (error.response && error.response.status === 401 &&
            error.config?.url?.indexOf('/v1/integration/') > -1 &&
            window.location.pathname.indexOf('/pos') > -1) {
            localStorage.removeItem('posDevice');
            localStorage.removeItem('posCashier');
            if (window.location.pathname !== '/pos') {
                window.location.href = '/pos';
            }
        }
        return Promise.reject(error);
    }
);

export default axios;

